// Server-authoritative combat, client side. The server resolves the fight and
// broadcasts:
//   • match:diff. Board state (attacker tap, currentLife, deaths→cemetery)
//                    AND the durable `combat` prompt record (state.combat).
//   • match:combat. The resolution envelope { events, combatId, attackerId,
//                    deaths } for the strike scene + trigger attribution.
//
// This module folds both into the EXISTING combat UI without ever running the
// local CombatEngine (the auth path is the sole resolver. No double-resolution).
//
// `applyCombatFromMirror` renders the prompt from `mirror.combat`; the local
// CombatEngine never opens it on the auth path, so this is the only opener.
// `onAuthCombatEvents` plays the resolution scene from the envelope.

import { t } from '../../../localization/t';
import { msg } from '../../../utils/game/logMessage';

// Build the client `pendingCombat`-shaped record from the server's mirror
// record. The server's buildRecord already matches the client record field
// for field; only actingSeat is client-derived (seat geometry is local).
function withActingSeat(host, rec) {
  const fight = host._fightFromPending(rec);
  const promptKind = host._promptKindForPendingPhase(rec.phase);
  const actingSeat = host._combatActingSeat(
    { kind: promptKind, target: rec.target, candidates: rec.interceptCandidates },
    fight,
  );
  return { pc: { ...rec, actingSeat }, promptKind, actingSeat };
}

// Render the combat prompt from the authoritative mirror record. Idempotent via
// a signature guard so unrelated diffs (a position update, a tap) don't re-open
// or flicker the prompt.
export function applyCombatFromMirror(host, mirror) {
  if (!host._authZonesEnabled()) return;
  const rec = mirror?.combat || null;
  const sig = rec ? `${rec.combatId}:${rec.phase}:${rec.seq || 0}` : null;
  if (sig === host._lastAuthCombatSig) return;
  host._lastAuthCombatSig = sig;
  // Stamp WHEN this prompt frame was shown so the reconcile tick can tell a fresh
  // mid-fight wait (opponent still deciding) from a genuine stall (a dropped
  // terminal frame). Only updated on a real sig change (the early-return above
  // skips redundant folds), so a still-live fight's stamp ages monotonically.
  host._combatLockShownAt = Date.now();

  // No server fight → tear down a SERVER-driven prompt (defend / assign /
  // intercept / waiting) only. Never the local pre-declare UI: `move_or_attack`
  // is the mover's own choice with no server record yet, and the attack target
  // modal is local too. A non-combat diff must not close them.
  if (!rec || !rec.combatId || !rec.attacker?.instanceId) {
    const k = host._activeCombatPrompt?.kind;
    // Clear the durable record. `combatStageBusy` (the pile-departure gate)
    // treats a non-'resolved' pendingCombat as a fight still on stage. Leaving
    // it set after the fight ends strands every casualty's fly-to-cemetery
    // forever (it gets force-disposed instead of landing in the pile).
    const hadRecord = !!host.state.gameState?.pendingCombat;
    if (hadRecord) host.setState((s) => ({ gameState: { ...s.gameState, pendingCombat: null } }));
    if (k && k !== 'move_or_attack') {
      // A staged fight closes its prompt record BEFORE its strikes commit:
      // the assignment panel stays up to morph into the first strike window
      // (onCombatStage tears it down if a non-strike stage comes first).
      const proc = mirror?.combatProcedure;
      const keepScene = !!(proc && proc.stage !== 'complete' && host.state.combatScene?.combatId === proc.combatId);
      host._closeCombatUI({ keepScene });
      host._maybePresentPendingGameOver?.();
    }
    // Release any casualty queued behind the combat gate now the fight is over.
    if (hadRecord) host._drainPileDepartures?.();
    return;
  }

  const { pc, promptKind, actingSeat } = withActingSeat(host, rec);
  host.setState((s) => ({ gameState: { ...s.gameState, pendingCombat: pc } }));
  // Replay: the durable record still folds (combatStageBusy gates casualty
  // pile departures off it), but no prompt UI ever opens. The fight was
  // already answered; even a "waiting" menu implies a live decision.
  if (host.props?.replayDriver) return;
  // Solo: the pilot answers BOTH seats' prompts (the answer intent carries the
  // acting seat via the envelope-seat provider), so nothing ever "waits".
  const localActs = host.props?.isCasualSandbox || actingSeat === host._localSeat();

  // Assignment lives on the combat overlay for BOTH seats (actor assigns, other
  // spectates the same panel).
  if (rec.phase === 'assigning') {
    host._activeCombatPrompt = {
      kind: localActs ? 'assign' : 'waiting',
      combatId: rec.combatId, actingSeat, attackerId: rec.attacker.instanceId,
    };
    host._syncAssignScene(pc, localActs);
    return;
  }

  if (localActs) {
    host._openActorPrompt({
      kind: promptKind, combatId: rec.combatId, actingSeat,
      attacker: rec.attacker, target: rec.target, candidates: rec.interceptCandidates,
    });
  } else {
    host._activeCombatPrompt = { kind: 'waiting', combatId: rec.combatId, actingSeat, attackerId: rec.attacker.instanceId };
    host._renderCombatMenu({ kind: 'waiting', label: t('Waiting for opponent...'), anchorId: rec.attacker.instanceId });
  }
}

// Fold the authoritative terminal field (`mirror.over`) into the client's
// game-over flow. The server is the single resolver of life loss (combat), so a
// player reaching 0 life stamps `state.over` on the same diff that carries the
// life change. Without this a no-interaction lethal (a Jinn site/avatar hit, a
// double-KO) folds the 0 life into the HUD but never enters the result flow, so
// both clients freeze post-combat. Idempotent via _declareGameOver's own
// _gameOverWinner guard, so a re-broadcast / reconnect Full never re-fires.
export function applyGameOverFromMirror(host, mirror) {
  if (!host._authZonesEnabled()) return;
  // Replay: the outcome is a chronicle row, not an event to present. Folding
  // it would pop the end-of-match overlay over the board on the final frame.
  // The winner is still tracked (silently, in sync with the seek position) so
  // combat-event enrichment can stamp DESTROYED on a lethal avatar hit.
  if (host.props?.replayDriver) {
    host._gameOverWinner = mirror?.over?.winner || null;
    return;
  }
  const over = mirror?.over;
  // Own idempotency latch: a draw (winner null) wouldn't trip _declareGameOver's
  // truthy _gameOverWinner guard, so a re-broadcast / reconnect Full would re-fire
  // it every diff. Latch on the field itself so it declares exactly once.
  if (!over || host._authGameOverApplied) return;
  host._authGameOverApplied = true;
  // winner null = double-KO draw; pass it through so the result flow shows a draw.
  host._declareGameOver(over.winner, over.reason || msg('death blow'));
  // A no-interaction site/avatar resolution clears combat without ever opening a
  // prompt, so present the verdict once the stage is clear (no combat scene up).
  host._maybePresentPendingGameOver?.();
}

// Map an authoritative seat winner to the local-relative value ArenaMatchResult
// takes as `autoWinner`. QA#48: this used to be a bare `winner === localSeat ?
// 'me' : 'opponent'`, which folded a draw or an unrecognised winner into
// 'opponent'. On BOTH clients, since neither seat equals null. The modal
// auto-files whatever it is handed, so two players each reported a loss and
// arbitration read the matching reports as a dispute. Anything that is not a
// recognised seat now returns null, which leaves the "Who won?" picker up.
export function autoWinnerForLocal(winner, localSeat) {
  if (winner !== 'p1' && winner !== 'p2') return null;
  return winner === localSeat ? 'me' : 'opponent';
}

// Play the resolution scene from the server's combat envelope. Idempotent per
// combatId (the scene player dedupes), so a reconnect re-broadcast is safe.
// Resolve a combatant's portrait info (name / image) for the scene. Prefers the
// live mesh; falls back to the authoritative mirror + card catalog when the card
// already left the board this resolution (a dead attacker is gone by now).
function combatInfo(host, id) {
  // isAvatar drives the tile caption: a defending avatar reads "Life loss"
  // (its assigned damage is life loss on its controller), never "Defender".
  const isAvatar = host._combatCardInstance?.(id)?.type === 'Avatar';
  const live = host._combatCardInfo(id);
  if (live) return { id, ...live, isAvatar };
  const mc = host._authMirror?.getState?.()?.cards?.[id];
  if (!mc?.cardId) return { id, name: t('Unit'), imageUrl: null, foiling: null, isSite: false, isAvatar };
  const byId = host._sorceryCardIndex
    || (host._sorceryCardIndex = new Map((host.props.sorceryCards || []).map((c) => [c.unique_id, c])));
  const cat = byId.get(mc.cardId);
  return {
    id,
    name: cat?.name || t('Unit'),
    imageUrl: cat?.printings?.[0]?.image_url || null,
    foiling: null,
    isSite: !!mc.isSite,
    isAvatar: isAvatar || mc.type === 'Avatar',
  };
}

// The mandatory-animation gate. A RESOLVED fight (identified by a combatId)
// ALWAYS stages a scene. Even a zero-damage resolution (ranged / undefended /
// fully-warded / 0-power), so both clients reach the resolved state and the
// attacker never strands the opponent on "Waiting for opponent." Only a payload
// with neither a combatId nor any events is a non-fight we drop. The 15s
// staleness skip applies to reconnect replays of OLD fights (events carry a `t`
// stamp); a fresh zero-event fight has no `t` and is never stale.
export function shouldStageResolvedFight({ combatId, events, now = Date.now() }) {
  const list = events || [];
  if (!combatId && !list.length) return false;
  if (!list.length) return true; // resolved fight, no damage → still mandatory
  const newest = list.reduce((m, e) => Math.max(m, e.t || now), 0);
  return now - newest <= 15000;
}

// Stamp raw resolution events with the display fields the CombatOverlay reads
// (targetName / targetImageUrl / targetFoiling / targetIsSite / targetDied /
// avatarSeat / magic). Pure over injected host accessors so it is shared
// identically by the local publishEvents hook AND this auth path. One source
// of enrichment, no drift. `deps`:
//   cardInstance(id)  → live-or-pile card { name, imageUrl, foiling, isSite,
//                        type } (swept cards must still resolve)
//   isCaster(id)      → spellcaster keyword test (decides the strike's `magic`)
//   isMeshGone(id)    → true when the card already left the board this beat
//   seatOf(card)      → 'p1' | 'p2'
//   findAvatar(seat)  → avatar portrait info for a lifeLoss anchor
//   gameOverWinner    → winning seat once the fight ends the game, else null
//   deaths            → optional Set of ids the resolution killed (auth path)
export function enrichCombatEvents(events, fight, deps) {
  const f = fight || {};
  const deaths = deps.deaths || null;
  const strikeMagic = (e) => {
    if (e.kind === 'lifeLoss') {
      // A lifeLoss beat carrying the ATTACKER's own instanceId is the
      // defenders' strike-back on an avatar attacker (qa#82): the strikers
      // are the defenders, not the attacker.
      if (e.instanceId && e.instanceId === f?.attacker?.instanceId) {
        return (f?.defenders || []).some((d) => deps.isCaster(d?.instanceId));
      }
      return deps.isCaster(f?.attacker?.instanceId);
    }
    if (e.sourceId) return deps.isCaster(e.sourceId);
    return (f?.defenders || []).some((d) => deps.isCaster(d?.instanceId));
  };
  const enrich = (e) => {
    if (e.kind === 'damage') {
      const inst = deps.cardInstance(e.targetId);
      return {
        ...e,
        magic: strikeMagic(e),
        targetName: inst?.name || t('Unit'),
        targetImageUrl: inst?.imageUrl || null,
        targetFoiling: inst?.foiling || null,
        targetIsSite: !!inst?.isSite,
        targetDied: (deaths ? deaths.has(e.targetId) : false)
          || deps.isMeshGone(e.targetId)
          || (inst?.type === 'Avatar' && !!deps.gameOverWinner && deps.gameOverWinner !== deps.seatOf(inst)),
        avatarSeat: inst?.type === 'Avatar' ? deps.seatOf(inst) : undefined,
      };
    }
    const site = !e.isAvatar && f?.target?.instanceId ? deps.cardInstance(f.target.instanceId) : null;
    const avatar = site ? null : deps.findAvatar(e.seat === 'p2' ? 2 : 1);
    return {
      ...e,
      magic: strikeMagic(e),
      targetName: site?.name || avatar?.name || t('Avatar'),

      targetImageUrl: site?.imageUrl || avatar?.imageUrl || null,
      targetFoiling: site?.foiling || avatar?.foiling || null,
      targetIsSite: site ? !!site.isSite : !!avatar?.isSite,
      targetDied: !!deps.gameOverWinner && deps.gameOverWinner !== e.seat,
    };
  };
  // Only strike beats are choreography. The server's event stream also
  // carries bookkeeping entries (kind:'log', e.g. "Lance breaks") that have
  // no target and no amount; letting one through staged a phantom defender
  // tile named after the fight's target with a "-undefined" damage burst.
  return (events || [])
    .filter((e) => e.kind === 'damage' || e.kind === 'lifeLoss')
    .map((e, i) => ({ ...enrich(e), i }));
}

export function onAuthCombatEvents(host, data) {
  if (!data?.combatId) return;
  // A staged fight never sends this envelope (its stages ride the diff); a
  // stray one for a fight the procedure owns must not start the old combined
  // playback on top of the staged windows.
  const proc = host._authMirror?.getState?.()?.combatProcedure;
  if (proc?.combatId === data.combatId) return;
  // Play the scene whenever a fight resolved. Even with no damage events, the
  // attack still animates (no silent graveyard). _playCombatScene dedupes per
  // combatId so a reconnect re-broadcast is safe. defenders come from the
  // envelope (the attacker's client never opened the defend prompt).
  const defenders = (data.defenders || []).map((id) => combatInfo(host, id));
  // The server's raw events carry no display fields (targetName / magic /
  // targetDied / avatarSeat). Those live client-side, in the CombatOverlay's
  // contract. Run them through the SAME enrichment the local publishEvents hook
  // uses so the auth path renders the full strike scene, not a stripped one.
  const fight = {
    attacker: { instanceId: data.attackerId },
    defenders: (data.defenders || []).map((id) => ({ instanceId: id })),
    // The server's classified attack target ({ kind, instanceId, seat }) anchors a
    // site/avatar life-loss beat on the correct card. An undefended SITE hit has no
    // defender to infer from, so without this the scene wrongly shows the avatar.
    // Fall back to the lone-defender guess for an older server with no target field.
    target: data.target || (data.defenders?.length === 1 ? { instanceId: data.defenders[0] } : undefined),
  };
  const events = host._enrichCombatEvents(data.events || [], fight, {
    // A swept card whose mesh is gone but that the envelope marked dead must
    // still stamp DESTROYED. The auth path knows the deaths up front, so it
    // augments the mesh-absence test with the envelope's death set.
    deaths: new Set(data.deaths || []),
  });
  host._playCombatScene({
    combatId: data.combatId,
    attackerId: data.attackerId,
    attackerInfo: combatInfo(host, data.attackerId),
    attackerDied: (data.deaths || []).includes(data.attackerId),
    defenders,
    events,
  });
  appendServerCombatLog(host, data);
  fireCombatTriggersFromEvents(host, data);
}

// Replay path: stage the RESULT of a journaled fight from its archived
// envelope. Same enrichment as the live path, but the scene mounts in
// `instant` mode (hits and deaths applied at once, no strike beats), no
// automation triggers fire (the journal already contains their outcomes),
// and no log lines append (the chronicle sidebar is the replay's log). The
// events' wall-clock `t` stamps are stripped so the staleness skip (built
// for reconnect re-broadcasts) never drops a historical fight.
export function onReplayCombatEnvelope(host, data) {
  if (!data?.combatId) return;
  // A journaled stage descriptor: only a strike window is a scene, staged
  // under its stage id so both windows of one fight play.
  if (data.stageId && data.kind !== 'strike') return;
  const defenders = (data.defenders || []).map((id) => combatInfo(host, id));
  const fight = {
    attacker: { instanceId: data.attackerId },
    defenders: (data.defenders || []).map((id) => ({ instanceId: id })),
    target: data.target || (data.defenders?.length === 1 ? { instanceId: data.defenders[0] } : undefined),
  };
  const events = host._enrichCombatEvents(
    (data.events || []).map(({ t, ...e }) => e),
    fight,
    { deaths: new Set(data.deaths || []) },
  );
  host._playCombatScene({
    combatId: data.combatId,
    stageKey: data.stageId || null,
    window: data.window || null,
    attackerId: data.attackerId,
    attackerInfo: combatInfo(host, data.attackerId),
    attackerDied: (data.deaths || []).includes(data.attackerId),
    defenders,
    events,
    instant: true,
  });
}

// One stage of a staged fight, from the commit it landed in. A strike window
// plays on the overlay (the presentation queue holds later commits until it
// is done); an effect step or the completion shows the board itself, so the
// assignment panel gives way. Returns true when a scene was put on stage.
export function onCombatStage(host, stage, m) {
  if (!stage?.stageId) return false;
  if (!host._playedStages) host._playedStages = new Set();
  if (host._playedStages.has(stage.stageId)) return false;
  host._playedStages.add(stage.stageId);
  appendServerCombatLog(host, { combatId: stage.stageId, log: stage.log });
  if (stage.kind !== 'strike') {
    const cs = host.state.combatScene;
    if (cs && cs.mode !== 'playback' && cs.combatId === stage.combatId) host._closeCombatUI();
    return false;
  }
  // The normal window shows the participants that still qualify: a defender
  // the first window killed is gone; one this window struck or killed shows.
  const struck = new Set((stage.events || []).flatMap((e) => [e.targetId, e.instanceId, e.sourceId]).filter(Boolean));
  const inPlay = (id) => m?.cards?.[id]?.zone === 'play';
  const defenderIds = (stage.defenders || []).filter((id) => stage.window !== 'normal'
    || inPlay(id) || (stage.deaths || []).includes(id) || struck.has(id));
  const fight = {
    attacker: { instanceId: stage.attackerId },
    defenders: defenderIds.map((id) => ({ instanceId: id })),
    target: stage.target || (defenderIds.length === 1 ? { instanceId: defenderIds[0] } : undefined),
  };
  const events = host._enrichCombatEvents(stage.events || [], fight, { deaths: new Set(stage.deaths || []) });
  return !!host._playCombatScene({
    combatId: stage.combatId,
    stageKey: stage.stageId,
    window: stage.window || null,
    attackerId: stage.attackerId,
    attackerInfo: combatInfo(host, stage.attackerId),
    attackerDied: (stage.deaths || []).includes(stage.attackerId),
    defenders: defenderIds.map((id) => combatInfo(host, id)),
    events,
  });
}

const STATUS_WAIT_SEAT = 'needs-choice';

// The persistent progress panel's model, derived from the DISPLAYED state:
// the step strip, the effect history with portraits, the outstanding choice
// and its owner. Idempotent per procedure revision + prompt.
export function applyCombatProgressFromMirror(host, m) {
  const proc = m?.combatProcedure || null;
  const cur = host.state.combatProgress || null;
  if (!proc) {
    if (cur) host.setState({ combatProgress: null });
    return;
  }
  const pc = m.pendingChoice || null;
  const sig = `${proc.combatId}:${proc.revision}:${proc.stage}:${pc?.promptId || ''}`;
  if (cur?.sig === sig) return;
  const choiceEffect = pc ? proc.effects.find((e) => e.promptId === pc.promptId) : null;
  const progress = {
    sig,
    combatId: proc.combatId,
    revision: proc.revision,
    stage: proc.stage,
    steps: proc.steps.map((st) => ({ id: st.id, status: st.status })),
    attacker: combatInfo(host, proc.fight?.attackerId),
    effects: proc.effects.map((e) => {
      const info = combatInfo(host, e.sourceId);
      return {
        id: e.id,
        kind: e.kind,
        wave: e.wave,
        parentId: e.parentId || null,
        sourceId: e.sourceId,
        name: info?.name || t('Unit'),
        imageUrl: info?.imageUrl || null,
        controller: e.controller,
        description: e.description || null,
        status: e.status,
        outcome: e.outcome || null,
        waitingOn: e.status === STATUS_WAIT_SEAT ? (pc?.promptId === e.promptId ? pc.seat : e.controller) : null,
      };
    }),
    choice: pc ? {
      promptId: pc.promptId,
      seat: pc.seat,
      effectId: choiceEffect?.id || null,
      ordering: pc.resume?.kind === 'combat_effect_order',
    } : null,
    terminal: proc.terminal || null,
    completedAt: proc.stage === 'complete' ? (cur?.combatId === proc.combatId && cur.completedAt ? cur.completedAt : Date.now()) : null,
  };
  host.setState({ combatProgress: progress });
}

// Append the server-authored, per-seat-REDACTED resolution log lines (strikes,
// deaths, site/avatar life loss). The server already redacted them for THIS
// seat, so they are rendered verbatim. Never re-derived from local state.
// Deduped per combatId so a reconnect re-broadcast of match:combat does not
// double-log (mirrors the _playCombatScene / _firedCombatTriggers guards).
function appendServerCombatLog(host, data) {
  const lines = data?.log;
  if (!Array.isArray(lines) || !lines.length) return;
  if (!host._loggedCombat) host._loggedCombat = new Set();
  if (host._loggedCombat.has(data.combatId)) return;
  host._loggedCombat.add(data.combatId);
  for (const line of lines) {
    if (!line?.text && typeof line?.key !== 'string') continue;
    const message = typeof line.key === 'string' ? { key: line.key, values: line.values } : line.text;
    host.addLogEntry(message, 'system', line.kind || 'system');
  }
}

// Fire the card-automation combat triggers (on_strike / on_damage_* / on_kill)
// in reaction to the AUTHORITATIVE server events. The local CombatEngine no
// longer fires them on the auth path. Each trigger fires on exactly ONE client:
// the controller of the trigger's SOURCE card, so the automation's own ops
// (which route through the auth command/intent primitives) apply once, not
// once-per-client.
//
// Out of scope here (deferred to server-side trigger dispatch): on_attack
// (pre-combat. Can't retroactively change a server-resolved fight) and
// on_death whose source already left the board this resolution (its mesh is
// gone by the time this runs). Both degrade to a logged no-op, never a crash.
export function fireCombatTriggersFromEvents(host, data) {
  const combatId = data?.combatId;
  if (!combatId) return;
  // Idempotency: a reconnect re-broadcast must not re-fire.
  if (!host._firedCombatTriggers) host._firedCombatTriggers = new Set();
  if (host._firedCombatTriggers.has(combatId)) return;
  host._firedCombatTriggers.add(combatId);

  // Each trigger fires on its source card's controller. One client per
  // trigger. The solo pilot IS both controllers, so it fires for every side
  // (the trigger intents carry the source card, so the envelope seat is the
  // source's controller; the server's idempotency keys dedupe re-sends).
  const local = host._localSeat();
  const mine = (id) => {
    const c = id && host._cardInstanceById(id);
    return c && (host.props?.isCasualSandbox || host._seatOf(c) === local) ? c : null;
  };

  const attacker = mine(data.attackerId);
  const deaths = new Set(data.deaths || []);

  // on_strike + on_kill are attributed to the attacker → its controller fires.
  if (attacker) {
    host._fireCombatTrigger('on_strike', attacker, { target: null });
    for (const deadId of deaths) {
      if (deadId === data.attackerId) continue;
      host._fireCombatTrigger('on_kill', attacker, { killed: deadId });
    }
  }

  // on_damage_dealt (attributed to the dealer) + on_damage_taken (to the
  // target). Each fires on its source card's controller.
  for (const ev of (data.events || [])) {
    if (ev.kind !== 'damage' || !(ev.amount > 0)) continue;
    const dealer = mine(ev.sourceId);
    if (dealer) host._fireCombatTrigger('on_damage_dealt', dealer, { defender: ev.targetId, amount: ev.amount });
    const taker = mine(ev.targetId);
    if (taker) host._fireCombatTrigger('on_damage_taken', taker, { source: ev.sourceId, amount: ev.amount });
  }
}
